import { circleFromDiameter, circleRing } from '../shared/circle-geometry.js';
import type { Point, Stroke } from '../types/app.js';
import { geodesicPoints, haversineKm } from './distance-math.js';
import { linePathPoints } from './line-geometry.js';

export function unwrapGlobeLongitude(lng: number, reference: number): number {
  return reference + ((lng - reference + 180) % 360 + 360) % 360 - 180;
}

export function globePath(points: readonly Point[], geodesic = true, closed = false): number[][] {
  if (!points.length) return [];
  const result: number[][] = [[points[0].lng, points[0].lat]];
  for (let i = 1; i < points.length + Number(closed); i++) {
    const a = points[(i - 1) % points.length];
    const b = points[i % points.length];
    const steps = Math.max(1, Math.ceil(haversineKm(a.lat, a.lng, b.lat, b.lng) / 55));
    const endLng = unwrapGlobeLongitude(b.lng, a.lng);
    const mercator = (lat: number) => Math.log(Math.tan(Math.PI / 4 + Math.max(-89.999999, Math.min(89.999999, lat)) * Math.PI / 360));
    const samples = geodesic
      ? geodesicPoints(a.lat, a.lng, b.lat, b.lng, steps)
      : Array.from({ length: steps + 1 }, (_, j) => ({
        lng: a.lng + (endLng - a.lng) * j / steps,
        lat: (2 * Math.atan(Math.exp(mercator(a.lat) + (mercator(b.lat) - mercator(a.lat)) * j / steps)) - Math.PI / 2) * 180 / Math.PI,
      }));
    for (const point of samples.slice(1)) result.push([unwrapGlobeLongitude(point.lng, result[result.length - 1][0]), point.lat]);
  }
  return result;
}

export function globeStrokePath(stroke: Stroke): number[][] {
  if (stroke.tool !== 'circle') return globePath(linePathPoints(stroke), stroke.geodesic !== false, stroke.tool === 'polygon');
  const { center, radiusKm } = circleFromDiameter(stroke.points, true);
  return circleRing(center, radiusKm, { earthRadiusKm: 6371, samples: 128, closed: true, wrapLongitude: false }).map(point => [point.lng, point.lat]);
}
